import { useEffect, useState } from "react";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { PageHeader } from "@/components/FilterBar";
import { api, download, errMsg, money } from "@/lib/api";

const ANNEES = Array.from({ length: 4 }, (_, i) => String(new Date().getFullYear() - i));
const COLS = [["tps_percue", "TPS perçue"], ["tvq_percue", "TVQ perçue"], ["tps_deductible", "CTI (TPS)"], ["tvq_deductible", "RTI (TVQ)"],
  ["net_tps", "TPS nette"], ["net_tvq", "TVQ nette"], ["net", "Taxe nette"], ["paye_net", "Payé à RQ"], ["ecart", "Écart"]];

const Boutons = ({ annee, q }) => {
  const [envoi, setEnvoi] = useState(null);
  const nom = `Déclaration TPS-TVQ ${q ? `T${q} ` : ""}${annee}`;
  const exporter = async (format) => {
    setEnvoi(format);
    try { await download("/declaration-taxes/rapport", { annee, trimestre: q || undefined, format }, `${nom}.${format}`); } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(null); }
  };
  const id = q ? `T${q}` : "annee";
  return (
    <div className="flex justify-end gap-1">
      <Button size="sm" variant="outline" className="h-7 px-2" disabled={!!envoi} onClick={() => exporter("pdf")} data-testid={`declaration-${id}-pdf`}>
        {envoi === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />}PDF</Button>
      <Button size="sm" variant="outline" className="h-7 px-2" disabled={!!envoi} onClick={() => exporter("xlsx")} data-testid={`declaration-${id}-excel`}>
        {envoi === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />}Excel</Button>
    </div>
  );
};

const Ligne = ({ t, annee, q, fort }) => (
  <tr className={`border-t ${fort ? "border-slate-300 font-semibold" : "border-slate-100"}`} data-testid={`declaration-ligne-${q ? `T${q}` : "annee"}`}>
    <td className="py-2 pr-2 whitespace-nowrap sticky left-0 bg-white">{q ? `T${q}` : "Année"}</td>
    <td className="py-2 px-2 sm:hidden"><Boutons annee={annee} q={q} /></td>
    {COLS.map(([k]) => <td key={k} className={`py-2 px-2 text-right tabular whitespace-nowrap ${k === "ecart" && Math.abs(t[k]) > 0.01 ? (t[k] > 0 ? "text-sky-700" : "text-red-600") : ""}`}>{money(t[k])}</td>)}
    <td className="hidden py-2 pl-2 sm:table-cell"><Boutons annee={annee} q={q} /></td>
  </tr>
);

// Rapports PDF et Excel de la déclaration TPS & TVQ, par trimestre et pour l'année
export default function DeclarationTaxes() {
  const [annee, setAnnee] = useState(ANNEES[0]);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/declaration-taxes", { params: { annee } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [annee]);
  const action = (
    <Select value={annee} onValueChange={(a) => { setD(null); setAnnee(a); }}>
      <SelectTrigger className="h-8 w-24" data-testid="declaration-annee"><SelectValue /></SelectTrigger>
      <SelectContent>{ANNEES.map((a) => <SelectItem key={a} value={a} data-testid={`declaration-annee-${a}`}>{a}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div data-testid="declaration-page">
      <PageHeader eyebrow="Taxes" title="Déclaration TPS & TVQ" />
      <Panel title={`Trimestres ${annee}`} action={action} testId="declaration-panel">
        <p className="mb-3 text-xs text-slate-500">Chaque rapport contient le sommaire à recopier dans la déclaration (taxes perçues, CTI / RTI, taxe nette), les paiements à Revenu Québec comptés pour la période avec l'écart, et le détail de chaque opération avec sa TPS et sa TVQ.</p>
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-slate-500"><th className="pb-1 text-left">Période</th><th className="pb-1 px-2 text-right sm:hidden">Rapports</th>{COLS.map(([k, l]) => <th key={k} className="pb-1 px-2 text-right whitespace-nowrap">{l}</th>)}<th className="hidden pb-1 pl-2 text-right sm:table-cell">Rapports</th></tr></thead>
              <tbody>
                {d.trimestres.map((t) => <Ligne key={t.trimestre} t={t} annee={annee} q={t.trimestre} />)}
                <Ligne t={d.total} annee={annee} fort />
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
